import { countries } from 'stores/country';

const byTitle = (a, b) =>
  (a.title || '').toLowerCase() > (b.title || '').toLowerCase() ? 1 : -1;

// Order of the `countries` table, with anything unrecognised last rather than
// first -- indexOf alone returns -1, which would sort a bad country code ahead
// of every real market.
const countryRank = (country) => {
  const index = countries.indexOf(country);

  return index === -1 ? countries.length : index;
};

/**
 * The markets to show, for a country that may not be known yet.
 *
 * An unknown country means every market, not none. During SSR on an unprefixed
 * route there is no locale in the URL to seed the country from and localStorage
 * is client-only, so `country` is undefined -- and `c.country === undefined`
 * matched no card at all, which shipped an empty carousel to anything reading
 * the server's markup (MTTMY-2188).
 *
 * US markets come first in that case so the cards a US visitor keeps after
 * hydration are the ones the server already rendered, and the carousel drops
 * its tail instead of reshuffling.
 *
 * Lives in its own module rather than inside the component so it is a pure,
 * testable function with no React dependency -- the same reason the locale
 * parsing sits in lib/lang-utils instead of in AppRoot.
 *
 * @param {Array<{title?: string, country?: string}>} cards every market card
 * @param {string} [country] 'USA' or 'CAN', or falsy when not yet known
 * @returns {Array} a new array, sorted; never the caller's own
 */
export const getSlidesData = (cards, country) => {
  if (!country) {
    // Copied before sorting: `cards` comes from the layout data, and sorting in
    // place would reorder it for every other consumer on the page.
    return [...cards].sort(
      (a, b) =>
        countryRank(a.country) - countryRank(b.country) || byTitle(a, b),
    );
  }

  return cards.filter((c) => c.country === country).sort(byTitle);
};

export default getSlidesData;
